import type {EvStatsFor30Days} from '@di-shared/api/di/__generated_eu';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import {roundToFixed} from '@di-shared/utils';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';

export const EV_DAILY_GRAPH_MAX_LENGTH = 30;

/**
 * EV 주행 정보 리스트를 maxLength만큼 패딩
 * @param evDrivingInfoList - 원본 EV 주행 정보 리스트
 * @param maxLength - 목표 길이 (기본값: 30)
 * @returns 패딩된 EV 주행 정보 리스트
 */
function padEvDrivingInfoList(evDrivingInfoList: EvStatsFor30Days['evDrivingInfoList'], maxLength: number = EV_DAILY_GRAPH_MAX_LENGTH) {
  if (evDrivingInfoList.length === 0 || maxLength <= 0) {
    return evDrivingInfoList;
  }

  const latestDate = evDrivingInfoList.reduce(
    (latest, current) => (current.date > latest ? current.date : latest),
    evDrivingInfoList[0].date
  );
  const parsedLatestDate = parseDateWithFormat(latestDate);

  if (!parsedLatestDate) {
    return evDrivingInfoList;
  }

  const evDrivingInfoMap = new Map(evDrivingInfoList.map((evDrivingInfo) => [evDrivingInfo.date, evDrivingInfo]));

  return Array.from({length: maxLength}, (_, index) => {
    const targetDate = subDate(parsedLatestDate, {days: maxLength - index - 1});
    const targetDateString = getYYYYMMddFormat(targetDate);

    return (
      evDrivingInfoMap.get(targetDateString) ?? {
        date: targetDateString,
        powerConsumption: 0.0,
        avrMileage: 0.0,
        regenPower: 0.0,
      }
    );
  });
}

export function useEnergyUsageDaily({evDailyData}: {evDailyData: EvStatsFor30Days}) {
  const isDailyEnergyUsageNone =
    evDailyData.evStatistics.totalPowerConsumption === 0 &&
    evDailyData.evStatistics.totalRegenPower === 0 &&
    evDailyData.evStatistics.avrMileage === 0;

  const actualDaysCount = evDailyData.evDrivingInfoList.length;

  /**
   * 변경 배경
   * @link https://hmg.atlassian.net/browse/DRIVINGINS-2183
   * 가입일자가 30일 미만인 사용자의 경우, 기존에는 무조건 30으로 나눠서 평균을 계산하여 실제보다 평균값이 작게 표시되는 문제
   * 최신 날짜 기준 최근 30일 캘린더 윈도우를 만들고, 비어 있는 날짜는 0으로 채움
   */
  const paddedEvDrivingInfoList = padEvDrivingInfoList(evDailyData.evDrivingInfoList, EV_DAILY_GRAPH_MAX_LENGTH);

  const verifiedEvDrivingInfoList = paddedEvDrivingInfoList
    .map((ev) => {
      const verifiedDate = parseDateWithFormat(ev.date);
      if (!verifiedDate) {
        throw new ExpectedError('evDrivingInfoList date parse error');
      }
      return {
        ...ev,
        verifiedDate,
      };
    })
    .sort((a, b) => +a.date - +b.date);

  /**
   * 전력 소비량 가공
   * @link https://jira.hmg-corp.io/browse/DRIVINGINS-789
   * evDailyData.evDrivingInfoList.reduce((acc, cur) => acc + cur.powerConsumption, 0)
   * 더한 값과 totalPowerConsumption이 일치하지 않음
   *
   * 가입일이 30일 미만인 경우 실제 일수로 나눔
   */
  const avgPowerConsumption = roundToFixed(evDailyData.evStatistics.totalPowerConsumption / actualDaysCount, 2);

  // min/max는 실제 데이터만 고려 (더미 데이터 제외)
  const actualEvDrivingInfoList = evDailyData.evDrivingInfoList;

  const minPowerConsumption = actualEvDrivingInfoList.filter((ev) => ev.powerConsumption !== 0).length
    ? Math.min(...actualEvDrivingInfoList.filter((ev) => ev.powerConsumption !== 0).map((ev) => ev.powerConsumption))
    : 0;
  const maxPowerConsumption = actualEvDrivingInfoList.length ? Math.max(...actualEvDrivingInfoList.map((ev) => ev.powerConsumption)) : 0;

  // 평균 전비 가공
  const minAvrMileage = actualEvDrivingInfoList.filter((ev) => ev.avrMileage !== 0).length
    ? Math.min(...actualEvDrivingInfoList.filter((ev) => ev.avrMileage !== 0).map((ev) => ev.avrMileage))
    : 0;
  const maxAvrMileage = actualEvDrivingInfoList.length ? Math.max(...actualEvDrivingInfoList.map((ev) => ev.avrMileage)) : 0;

  return {
    isDailyEnergyUsageNone,
    verifiedEvDrivingInfoList,
    avgPowerConsumption,
    minPowerConsumption,
    maxPowerConsumption,
    minAvrMileage,
    maxAvrMileage,
  };
}
